import HealthSourceInfo from "@/components/health/HealthSourceInfo";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Clock, Flame, Dumbbell, Square, Loader2 } from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/AuthContext";
import {
  useActiveWorkout,
  formatElapsed,
  estimateCaloriesBurned,
} from "@/lib/ActiveWorkoutContext";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";

/**
 * Sheet/modal popped from the floating timer pill while a workout is active.
 * Shows live duration, calorie estimate, exercises completed vs total, and
 * estimated time remaining. The big red END WORKOUT button triggers the
 * confirm → log → summary flow.
 *
 * `exercisesCompleted` is read from the dashboard query so the UI stays
 * consistent without any duplicate query.
 */
export default function ActiveWorkoutDialog({
  open,
  onOpenChange,
  exercisesCompleted = 0,
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { active, elapsedSeconds, endWorkout, setSummary } = useActiveWorkout();
  const [confirmEnd, setConfirmEnd] = useState(false);

  // Convert weight to kg for the calorie estimator if needed.
  const weightKg =
    user?.weight_kg ||
    (typeof user?.current_weight === "number"
      ? user.use_lbs === false
        ? user.current_weight
        : user.current_weight * 0.45359237
      : null);

  const calories = estimateCaloriesBurned(weightKg, elapsedSeconds);

  const totalExercises = active?.total_exercises || 0;
  const remainingExercises = Math.max(0, totalExercises - exercisesCompleted);
  const avgMinutesPerExercise =
    totalExercises > 0 ? (active?.estimated_minutes || 30) / totalExercises : 5;
  const remainingSeconds = Math.max(
    0,
    Math.round(remainingExercises * avgMinutesPerExercise * 60),
  );

  const saveSessionMutation = useMutation({
    mutationFn: (payload) => base44.entities.WorkoutSession.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workout-sessions"] });
    },
  });

  const handleEnd = async () => {
    if (!active) return;
    const endedAt = new Date().toISOString();
    const finalSummary = {
      plan_name: active.plan_name,
      duration_seconds: elapsedSeconds,
      calories_burned: calories,
      exercises_completed: exercisesCompleted,
      total_exercises: totalExercises,
    };
    // Fire-and-display: we log the session, show the summary, and clear the
    // active state. If logging fails we still let the user finish — the
    // worst case is a missing history row.
    try {
      await saveSessionMutation.mutateAsync({
        user_id: user?.id,
        workout_plan_id: active.plan_id,
        plan_name: active.plan_name,
        started_at: active.started_at,
        ended_at: endedAt,
        duration_seconds: elapsedSeconds,
        calories_burned: calories,
        exercises_completed: exercisesCompleted,
        total_exercises: totalExercises,
        completed_date: format(new Date(), "yyyy-MM-dd"),
      });
    } catch (_e) {
      toast.error("Workout ended but couldn't be saved to history.");
    }
    setConfirmEnd(false);
    endWorkout();
    onOpenChange(false);
    setSummary(finalSummary);
  };

  const handleDiscard = () => {
    endWorkout();
    onOpenChange(false);
    toast.info("Workout cancelled and timer stopped.");
  };

  if (!active) return null;

  return (
    <>
      <Dialog open={open && !!active} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md" data-testid="active-workout-dialog">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-display">Workout Active</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Duration block */}
            <div className="rounded-2xl border border-white/10 p-5 space-y-1">
              <p className="text-sm text-white/60">Duration</p>
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-emerald-400" />
                <p
                  className="font-mono font-bold text-3xl text-emerald-400 tracking-tight"
                  data-testid="active-workout-duration"
                >
                  {formatElapsed(elapsedSeconds)}
                </p>
              </div>
            </div>

            {/* Stat rows */}
            <StatRow
              icon={<Flame className="w-5 h-5 text-orange-400" />}
              label={<>Calories Burned<HealthSourceInfo topic="exercise" /></>}
              value={`${calories}`}
              suffix=" kcal"
              testId="active-workout-calories"
            />
            <StatRow
              icon={<Dumbbell className="w-5 h-5 text-emerald-400" />}
              label="Exercises Completed"
              value={`${exercisesCompleted}`}
              suffix={` of ${totalExercises}`}
              testId="active-workout-exercises"
            />
            <StatRow
              icon={<Clock className="w-5 h-5 text-sky-400" />}
              label="Est. Time Remaining"
              value={formatMmSs(remainingSeconds)}
              testId="active-workout-remaining"
            />

            <div className="flex flex-col gap-2 pt-1">
              <Button
                type="button"
                onClick={() => setConfirmEnd(true)}
                className="w-full h-11 gap-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold uppercase tracking-wide"
                data-testid="end-workout-btn"
              >
                <Square className="w-4 h-4" fill="currentColor" /> End & Save Workout
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleDiscard}
                className="w-full h-9 text-xs text-white/50 hover:text-white/80 hover:bg-white/5"
                data-testid="discard-workout-btn"
              >
                Discard / Stop Timer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmEnd} onOpenChange={setConfirmEnd}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End workout?</AlertDialogTitle>
            <AlertDialogDescription>
              This will stop the timer and save the session to your history.
              You can&apos;t undo this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="end-workout-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEnd}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="end-workout-confirm"
              disabled={saveSessionMutation.isPending}
            >
              {saveSessionMutation.isPending && (
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              )}
              End workout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function StatRow({ icon, label, value, suffix = "", testId }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/10 p-4">
      <div className="shrink-0">{icon}</div>
      <p className="flex-1 text-sm text-white/70">{label}</p>
      <p className="text-right" data-testid={testId}>
        <span className="font-bold text-lg">{value}</span>
        {suffix && <span className="text-xs text-white/50">{suffix}</span>}
      </p>
    </div>
  );
}

function formatMmSs(seconds) {
  const total = Math.max(0, Math.floor(seconds));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

/**
 * Standalone summary dialog. Reads the just-finished workout from the
 * `ActiveWorkoutContext` so it can survive the `WorkoutTimerPill` unmount
 * triggered by clearing `active`. Mount this once at the app root.
 */
export function WorkoutSummaryDialog() {
  const { summary, clearSummary } = useActiveWorkout();
  if (!summary) return null;
  return (
    <Dialog open={!!summary} onOpenChange={(o) => !o && clearSummary()}>
      <DialogContent className="max-w-sm" data-testid="workout-summary-dialog">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-display">
            Workout Complete!
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-center">
          <p className="text-sm text-muted-foreground">{summary.plan_name}</p>
          <div className="grid grid-cols-3 gap-2 py-3">
            <div className="rounded-xl bg-white/5 p-3">
              <p className="text-2xl font-bold font-mono text-emerald-400">
                {formatElapsed(summary.duration_seconds).replace(/^00:/, "")}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-white/50 mt-1">
                Duration
              </p>
            </div>
            <div className="rounded-xl bg-white/5 p-3">
              <p className="text-2xl font-bold text-orange-400">
                {summary.calories_burned}<HealthSourceInfo topic="exercise" />
              </p>
              <p className="text-[10px] uppercase tracking-wider text-white/50 mt-1">
                kcal
              </p>
            </div>
            <div className="rounded-xl bg-white/5 p-3">
              <p className="text-2xl font-bold text-sky-400">
                {summary.exercises_completed}/{summary.total_exercises}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-white/50 mt-1">
                Exercises
              </p>
            </div>
          </div>
          <Button
            onClick={clearSummary}
            className="w-full"
            data-testid="workout-summary-done-btn"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
